// src/App.jsx
import React, { useMemo, useState, useEffect } from "react";
import { AppBar, Avatar, Badge, Box, Button, CssBaseline, IconButton, Link, Stack, ThemeProvider, Toolbar, Typography } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { BrowserRouter, Routes, Route, Navigate, Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { LocaleProvider, useMessage } from "./i18n/locale";
import { makeTheme } from "./theme";
import NetworkProgress from "./components/NetworkProgress";
import LoginModal from "./components/LoginModal";
import AppVersion from "./components/AppVersion";
import LoadingSpinner from "./components/LoadingSpinner";
import { getCurrentLocation } from "./services/location";
import { getAuthGateState, hasPermission, isAuthenticatedUser, PERMISSIONS } from "./services/authRoles";
import { authService, messageService } from "./services/resources.service";
import { normalizeThreads } from "./services/messaging";
import { clearSessionToken, getSessionToken } from "./services/authSession";
import { buildSignInPath } from "./services/authRedirect";

// Pages
import Home from "./pages/Home.jsx";
import EventsIndex from "./pages/EventsIndex.jsx";
import EventDetails from "./pages/EventDetails.jsx";
import GigBoard from "./pages/GigBoard.jsx";
import ArtistsIndex from "./pages/ArtistsIndex.jsx";
import ArtistProfile from "./pages/ArtistProfile.jsx";
import VenuesIndex from "./pages/VenuesIndex.jsx";
import VenueProfile from "./pages/VenueProfile.jsx";
import StudiosIndex from "./pages/StudiosIndex.jsx";  
import StudioProfile from "./pages/StudioProfile.jsx";
import AuthLanding from "./pages/AuthLanding.jsx";
import SignUp from "./pages/SignUp.jsx";
import ActivateAccount from "./pages/ActivateAccount.jsx";
import Profile from "./pages/Profile.jsx";
import ResetPassword from "./pages/ResetPassword.jsx";
import LegalDocument from "./pages/LegalDocument.jsx";
import { Admin, CreateBundle, CreatePage, Inbox, NotFound, PostGig } from "./pages/WorkflowScreens.jsx";

function useAuth() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const token = getSessionToken();
    if (!token) {
      setAuthLoading(false);
      return () => {
        active = false;
      };
    }

    authService.me(token).then((authenticatedUser) => {
      if (active && isAuthenticatedUser(authenticatedUser)) setUser(authenticatedUser);
    }).catch(() => {
      clearSessionToken();
    }).finally(() => {
      if (active) setAuthLoading(false);
    });

    return () => {
      active = false;
    };
  }, []);
  return { user, setUser, authLoading };
}

// ------------------ Scroll to top on route change ------------------
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);
  return null;
}

function GlobalBrandHeader({ user, onLogout, unreadMessages = 0 }) {
  const location = useLocation();
  const signIn = useMessage("authSignIn");
  const headerSlogan = useMessage("headerSlogan");
  const profile = useMessage("profile");
  const inbox = useMessage("inbox");
  const logout = useMessage("authLogout");
  const role = useMessage(`accountRole${user?.role || "Unknown"}`);
  const displayName = user?.name || user?.email || profile;

  return (
    <AppBar position="fixed" elevation={0} color="inherit" sx={{ zIndex: (theme) => theme.zIndex.appBar + 1 }}>
      <Toolbar sx={{ minHeight: "56px !important", px: { xs: 1.5, sm: 2 } }}>
        <Stack
          component={RouterLink}
          to="/"
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            color: "inherit",
            textDecoration: "none",
            borderRadius: 2,
            "&:focus-visible": {
              outline: "3px solid",
              outlineColor: "primary.main",
              outlineOffset: 3,
            },
          }}
        >
          <Avatar src="/logo.png" alt="" variant="rounded" sx={{ width: 38, height: 38, borderRadius: 2 }} />
          <Stack spacing={0}>
            <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: 0.2, lineHeight: 1.05 }}>
              Band-Joe
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: { xs: "none", sm: "block" }, lineHeight: 1.15 }}
            >
              {headerSlogan}
            </Typography>
          </Stack>
        </Stack>
        <Box sx={{ flex: 1 }} />
        {!user && (
          <Button component={RouterLink} to={buildSignInPath(location)} variant="contained">
            {signIn}
          </Button>
        )}
        {user && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {hasPermission(user, PERMISSIONS.INBOX) && <IconButton
              component={RouterLink}
              to="/inbox"
              aria-label={inbox}
              title={inbox}
              color="inherit"
            >
              <Badge badgeContent={unreadMessages} color="error" max={99}>
                <MailOutlineIcon />
              </Badge>
            </IconButton>}
            <Button
              component={RouterLink}
              to="/me"
              color="inherit"
              aria-label={profile}
              sx={{ minWidth: 0, px: { xs: 0.5, sm: 1 } }}
            >
              <Avatar src={user.avatar} sx={{ width: 32, height: 32, fontSize: 14 }}>
                {displayName.slice(0, 1).toUpperCase()}
              </Avatar>
              <Stack
                component="span"
                sx={{ display: { xs: "none", sm: "flex" }, ml: 1, maxWidth: 160, alignItems: "flex-start" }}
              >
                <Typography component="span" variant="body2" fontWeight={800} noWrap sx={{ maxWidth: "100%" }}>
                  {displayName}
                </Typography>
                <Typography component="span" variant="caption" color="text.secondary" noWrap>
                  {role}
                </Typography>
              </Stack>
            </Button>
            <IconButton onClick={onLogout} aria-label={logout} title={logout} color="inherit">
              <LogoutIcon />
            </IconButton>
          </Stack>
        )}
      </Toolbar>
    </AppBar>
  );
}

// ------------------ Route Guards ------------------
function RequireAuth({ user, authLoading = false, children }) {
  const location = useLocation();
  const gateState = getAuthGateState(user, { loading: authLoading });
  if (gateState === "loading") return <LoadingSpinner minHeight="50dvh" />;
  if (gateState === "sign-in") return <Navigate to={`/auth?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return children;
}

function RequirePermission({ user, authLoading = false, permission, children }) {
  const location = useLocation();
  const gateState = getAuthGateState(user, { loading: authLoading, permission });
  if (gateState === "loading") return <LoadingSpinner minHeight="50dvh" />;
  if (gateState === "sign-in") return <Navigate to={`/auth?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (gateState === "forbidden") return <Navigate to="/" replace />;
  return children;
}

function DeveloperCredit() {
  const developedBy = useMessage("developedBy");
  const terms = useMessage("legalTerms");
  const privacy = useMessage("legalPrivacy");

  return (
    <Box
      component="footer"
      sx={{
        position: "fixed",
        insetInline: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        borderBlockStart: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxSizing: "border-box",
        height: { xs: "min(32px, 10dvh)", sm: "auto" },
        maxHeight: { xs: "10dvh", sm: "none" },
        overflow: "hidden",
        py: { xs: 0.5, sm: 2.5 },
        px: { xs: 1, sm: 2 },
        textAlign: "center",
      }}
    >
      <Stack direction="row" justifyContent="center" alignItems="center" spacing={{ xs: 1, sm: 2 }} sx={{ height: "100%", whiteSpace: "nowrap" }}>
        <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
          {developedBy}{" "}
          <Link href="https://www.quantummindsoftware.com/" target="_blank" rel="noopener noreferrer" color="inherit" fontWeight={700}>
            QuantumMind Software
          </Link>
        </Typography>
        <Stack direction="row" spacing={{ xs: 1, sm: 2 }}>
          <Link component={RouterLink} to="/terms" color="text.secondary" sx={{ fontSize: { xs: "0.75rem", sm: "1rem" } }}>{terms}</Link>
          <Link component={RouterLink} to="/privacy" color="text.secondary" sx={{ fontSize: { xs: "0.75rem", sm: "1rem" } }}>{privacy}</Link>
        </Stack>
        <AppVersion sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />
      </Stack>
    </Box>
  );
}

// ------------------ Layout Router ------------------
function AppRouter() {
  const { user, setUser, authLoading } = useAuth();
  const navigate = useNavigate();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [loginRequest, setLoginRequest] = useState({
    open: false,
    redirectTo: "/",
    reasonKey: "loginModalDefaultReason",
  });

  const requestLogin = ({ redirectTo = "/inbox", reasonKey = "loginModalDefaultReason" } = {}) => {
    setLoginRequest({ open: true, redirectTo, reasonKey });
  };

  const finishModalLogin = (signedInUser) => {
    setUser(signedInUser);
    setLoginRequest((request) => ({ ...request, open: false }));
    navigate(loginRequest.redirectTo);
  };

  const logout = () => {
    clearSessionToken();
    setUser(null);
    setUnreadMessages(0);
    navigate("/", { replace: true });
  };

  const refreshUnreadMessages = React.useCallback(() => {
    if (!user || !hasPermission(user, PERMISSIONS.INBOX)) {
      setUnreadMessages(0);
      return Promise.resolve();
    }
    return messageService.threads()
      .then((payload) => {
        const count = normalizeThreads(payload).reduce(
          (total, thread) => total + Math.max(0, Number(thread.unreadCount) || 0),
          0,
        );
        setUnreadMessages(count);
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user || !hasPermission(user, PERMISSIONS.INBOX)) {
      setUnreadMessages(0);
      return undefined;
    }
    refreshUnreadMessages();
    const onFocus = () => refreshUnreadMessages();
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
    };
  }, [user, refreshUnreadMessages]);

  // Region management (Country > Province > City; MVP: single chip/display string)
  const [region, setRegion] = useState("Anywhere");
  // Session-only discovery filter: every fresh app load starts with Near Me off,
  // while client-side navigation keeps the user's current radius selection.
  const [discoveryRadiusKm, setDiscoveryRadiusKm] = useState(null);
  // Device coordinates are intentionally memory-only. Restoring them from
  // storage could make "Near me" use a position from a previous session.
  const [discoveryLocation, setDiscoveryLocation] = useState(null);
  useEffect(() => {
    localStorage.removeItem("bandjoe_discovery_location");
  }, []);
  const handleChangeRegion = async () => {
    try {
      const location = await getCurrentLocation();
      setDiscoveryLocation(location);
      setRegion("Near me");
      setDiscoveryRadiusKm(25);
      return location;
    } catch {
      // Keep the current discovery region when permission is denied or
      // location services are unavailable. A later click retries the request.
      return null;
    }
  };

  const [colorMode] = useState(() => localStorage.getItem("bandjoe_theme") || "light");
  const theme = useMemo(() => makeTheme(colorMode), [colorMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: "100dvh", display: "flex", flexDirection: "column" }}>
      <LocaleProvider region={region}>
      <NetworkProgress />
      <ScrollToTop />
      <GlobalBrandHeader user={user} onLogout={logout} unreadMessages={unreadMessages} />
      <Box component="main" sx={{ pt: 7, pb: { xs: "min(32px, 10dvh)", sm: 9 }, flex: 1 }}>
      <Routes>
        {/* Home (passes navigation + region controls) */}
        <Route
          path="/"
          element={
            <Home
              onNavigate={navigate}
              user={user}
              region={region}
              onChangeRegion={handleChangeRegion}
              discoveryLocation={discoveryLocation}
              discoveryRadiusKm={discoveryRadiusKm}
              onChangeDiscoveryRadius={setDiscoveryRadiusKm}
            />
          }
        />

        {/* Events */}
        <Route path="/events" element={<EventsIndex
              onNavigate={navigate}
              user={user}
              region={region}
              onChangeRegion={handleChangeRegion}
              discoveryLocation={discoveryLocation}
              discoveryRadiusKm={discoveryRadiusKm}
              onChangeDiscoveryRadius={setDiscoveryRadiusKm} />} />
        <Route path="/events/gigs" element={<GigBoard
              onNavigate={navigate}
              user={user}
              region={region}
              onChangeRegion={handleChangeRegion}
              discoveryLocation={discoveryLocation}
              discoveryRadiusKm={discoveryRadiusKm}
              onChangeDiscoveryRadius={setDiscoveryRadiusKm} />} />
        <Route path="/events/:id" element={<EventDetails
              onNavigate={navigate}
              user={user}
              region={region}
              onChangeRegion={handleChangeRegion} />} />
        <Route
          path="/events/:eventId/edit"
          element={
            <RequirePermission user={user} authLoading={authLoading} permission={PERMISSIONS.POST_GIG}>
              <PostGig user={user} />
            </RequirePermission>
          }
        />

        {/* Artists */}
        <Route path="/artists" element={<ArtistsIndex
              onNavigate={navigate}
              user={user}
              region={region}
              onChangeRegion={handleChangeRegion}
              discoveryLocation={discoveryLocation}
              discoveryRadiusKm={discoveryRadiusKm}
              onChangeDiscoveryRadius={setDiscoveryRadiusKm}
              onRequireAuth={requestLogin} />} />
        <Route path="/artists/:id" element={<ArtistProfile
              onNavigate={navigate}
              user={user}
              region={region}
              onChangeRegion={handleChangeRegion}
              onRequireAuth={requestLogin} />} />

        {/* Venues */}
        <Route path="/venues" element={<VenuesIndex
              onNavigate={navigate}
              user={user}
              region={region}
              onChangeRegion={handleChangeRegion}
              discoveryLocation={discoveryLocation}
              discoveryRadiusKm={discoveryRadiusKm}
              onChangeDiscoveryRadius={setDiscoveryRadiusKm}
              onRequireAuth={requestLogin} />} />
        <Route path="/venues/:id" element={<VenueProfile
              onNavigate={navigate}
              user={user}
              region={region}
              onChangeRegion={handleChangeRegion}
              onRequireAuth={requestLogin} />} />
        <Route
          path="/venues/gig/new"
          element={
            <RequirePermission user={user} authLoading={authLoading} permission={PERMISSIONS.POST_GIG}>
              <PostGig user={user} />
            </RequirePermission>
          }
        />

        {/* Studios */}
        <Route path="/studios" element={<StudiosIndex
              onNavigate={navigate}
              user={user}
              region={region}
              onChangeRegion={handleChangeRegion}
              discoveryLocation={discoveryLocation}
              discoveryRadiusKm={discoveryRadiusKm}
              onChangeDiscoveryRadius={setDiscoveryRadiusKm}
              onRequireAuth={requestLogin} />} />
        <Route path="/studios/:id" element={<StudioProfile
              onNavigate={navigate}
              user={user}
              region={region}
              onChangeRegion={handleChangeRegion}
              onRequireAuth={requestLogin} />} />
        <Route path="/media/:id" element={<StudioProfile
              entityType="media"
              onNavigate={navigate}
              user={user}
              region={region}
              onChangeRegion={handleChangeRegion}
              onRequireAuth={requestLogin} />} />
        <Route
          onNavigate={navigate}
          path="/studios/bundles/new"
          element={
            <RequirePermission user={user} authLoading={authLoading} permission={PERMISSIONS.CREATE_STUDIO_BUNDLE}>
              <CreateBundle />
            </RequirePermission>
          }
        />

        {/* Messaging (sample protected route) */}
        <Route
          path="/inbox"
          onNavigate={navigate}
          element={
            <RequirePermission user={user} authLoading={authLoading} permission={PERMISSIONS.INBOX}>
              <Inbox user={user} onUnreadCountChange={refreshUnreadMessages} />
            </RequirePermission>
          }
        />

        {/* Auth */}
        <Route path="/auth" element={<AuthLanding 
              onNavigate={navigate} onAuth={setUser} />} />
        <Route path="/auth/signup" element={<SignUp 
              onNavigate={navigate} onAuth={setUser} />} />
        <Route path="/auth/activate" element={<ActivateAccount onAuth={setUser} />} />
        <Route path="/auth/reset" element={<ResetPassword 
              onNavigate={navigate} />} />
        <Route path="/terms" element={<LegalDocument type="terms" />} />
        <Route path="/privacy" element={<LegalDocument type="privacy" />} />

        {/* Profile */}
        <Route
          path="/me"
          element={
            <RequireAuth user={user} authLoading={authLoading}>
              <Profile user={user} 
              onNavigate={navigate} />
            </RequireAuth>
          }
        />

        <Route path="/artists/new" element={<RequirePermission user={user} authLoading={authLoading} permission={PERMISSIONS.CREATE_ARTIST_PAGE}><CreatePage kind="artist" /></RequirePermission>} />
        <Route path="/artists/:id/edit" element={<RequirePermission user={user} authLoading={authLoading} permission={PERMISSIONS.CREATE_ARTIST_PAGE}><CreatePage kind="artist" /></RequirePermission>} />
        <Route path="/venues/new" element={<RequirePermission user={user} authLoading={authLoading} permission={PERMISSIONS.CREATE_VENUE_PAGE}><CreatePage kind="venue" /></RequirePermission>} />
        <Route path="/venues/:id/edit" element={<RequirePermission user={user} authLoading={authLoading} permission={PERMISSIONS.CREATE_VENUE_PAGE}><CreatePage kind="venue" /></RequirePermission>} />
        <Route path="/studios/new" element={<RequirePermission user={user} authLoading={authLoading} permission={PERMISSIONS.CREATE_STUDIO_PAGE}><CreatePage kind="studio" /></RequirePermission>} />
        <Route path="/studios/:id/edit" element={<RequirePermission user={user} authLoading={authLoading} permission={PERMISSIONS.CREATE_STUDIO_PAGE}><CreatePage kind="studio" /></RequirePermission>} />
        <Route path="/media/new" element={<RequirePermission user={user} authLoading={authLoading} permission={PERMISSIONS.CREATE_MEDIA_PAGE}><CreatePage kind="media" /></RequirePermission>} />
        <Route path="/media/:id/edit" element={<RequirePermission user={user} authLoading={authLoading} permission={PERMISSIONS.CREATE_MEDIA_PAGE}><CreatePage kind="media" /></RequirePermission>} />
        <Route path="/admin" element={<RequirePermission user={user} authLoading={authLoading} permission={PERMISSIONS.ADMIN}><Admin /></RequirePermission>} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Box>
      <LoginModal
        open={loginRequest.open}
        reasonKey={loginRequest.reasonKey}
        onClose={() => setLoginRequest((request) => ({ ...request, open: false }))}
        onSignedIn={finishModalLogin}
      />
      <DeveloperCredit />
      </LocaleProvider>
      </Box>
    </ThemeProvider>
  );
}

// ------------------ App Root ------------------
export default function App() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}
